import { Link, useRouter } from "@tanstack/react-router"
import { useState } from "react"
import { useMutation, useQuery } from "convex/react"
import { Bell } from "lucide-react"
import { toast } from "sonner"
import { api } from "../../convex/_generated/api"
import { formatMessageRelative } from "@/lib/messages"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { cn } from "@/lib/utils"

const PANE_LIMIT = 8

type NotificationBellProps = {
  viewAllPath: "/recruit/notifications" | "/candidate/notifications"
}

export function NotificationBell({ viewAllPath }: NotificationBellProps) {
  const router = useRouter()
  const profile = useQuery(api.profiles.me)
  const notifications = useQuery(api.notifications.listMine, { limit: PANE_LIMIT })
  const unread = profile?.unreadNotificationCount ?? 0
  const markRead = useMutation(api.notifications.markRead)
  const markAllRead = useMutation(api.notifications.markAllRead)
  const [markingAll, setMarkingAll] = useState(false)

  const hasMore =
    (notifications?.length ?? 0) >= PANE_LIMIT || (unread ?? 0) > PANE_LIMIT

  const markEveryNotificationRead = async () => {
    setMarkingAll(true)
    try {
      // Bounded to protect against a pathological stream continually adding
      // new notifications while the menu is open; the remaining badge still
      // tells the person exactly what needs another click.
      for (let batch = 0; batch < 50; batch += 1) {
        const result = await markAllRead()
        if (!result.hasMore) return
      }
      toast.message("Some newer notifications are still unread")
    } catch {
      toast.error("Could not mark notifications as read")
    } finally {
      setMarkingAll(false)
    }
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="mkt-notifications-bell__trigger" aria-label="Notifications">
        <Bell className="size-[1.05rem]" aria-hidden />
        {!!unread && (
          <span className="mkt-notifications-bell__badge">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" sideOffset={8} className="mkt-notifications-pane">
        <div className="mkt-notifications-pane__head">
          <p className="mkt-notifications-pane__title">Notifications</p>
          {(unread ?? 0) > 0 && (
            <button
              type="button"
              className="mkt-notifications-pane__mark-all"
              onClick={() => void markEveryNotificationRead()}
              disabled={markingAll}
            >
              {markingAll ? "Marking…" : "Mark all read"}
            </button>
          )}
        </div>

        <div className="mkt-notifications-pane__body">
          {notifications === undefined ? (
            <p className="mkt-notifications-pane__empty">Loading…</p>
          ) : notifications.length === 0 ? (
            <p className="mkt-notifications-pane__empty">Nothing yet.</p>
          ) : (
            <ul className="mkt-notifications-pane__list">
              {notifications.map((n) => (
                <li key={n._id}>
                  <button
                    type="button"
                    className="mkt-notifications-pane__item"
                    onClick={() => {
                      void markRead({ notificationId: n._id })
                      if (n.link) router.history.push(n.link)
                    }}
                  >
                    <span
                      className={cn(
                        "mkt-notifications-pane__item-title",
                        !n.read && "mkt-notifications-pane__item-title--unread",
                      )}
                    >
                      {n.title}
                    </span>
                    {n.body && (
                      <span className="mkt-notifications-pane__item-body">{n.body}</span>
                    )}
                    <span className="mkt-notifications-pane__item-time">
                      {formatMessageRelative(n._creationTime)}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="mkt-notifications-pane__foot">
          <Link to={viewAllPath} className="mkt-notifications-pane__view-all">
            View all notifications
            {hasMore && (unread ?? 0) > 0 ? ` · ${unread} unread` : ""}
          </Link>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
